// Cost charts, one system (the dataviz rules): the six layers keep one fixed colour each everywhere (--series-1…5 in
// waterfall order, Other in the neutral), thin marks with a 2px surface gap between touching segments, 4px rounded data
// ends, a legend whenever there's more than one series, text in text colours (never the series colour), and every mark
// explains itself on hover or keyboard focus (Why). The numbers are always in the table beside the chart too.
import type { ReactNode } from "react";
import type { Stack } from "../../../../../shared/costs.ts";
import { LAYER_LABELS, LAYER_SHORT, LAYERS, type Layer } from "../../../../../shared/runs.ts";
import { centsText, usd4 } from "../../../lib/costs.ts";
import { cx } from "../../../ui/controls.tsx";
import { Why, type WhyLine } from "../../../ui/Why.tsx";

export const LAYER_COLOR: Record<Layer, string> = {
  materials: "var(--series-1)",
  assembly: "var(--series-2)",
  packaging: "var(--series-3)",
  freight: "var(--series-4)",
  tooling: "var(--series-5)",
  other: "var(--faint)",
};

/** What a layer of a stack is made of, as "why" lines. */
export const layerLines = (s: Stack, l: Layer): WhyLine[] => [
  ...s.why[l].map((p) => ({ label: p.label, value: usd4(p.usd), detail: p.detail })),
  { label: `${LAYER_LABELS[l]} per unit`, value: centsText(s.cents[l]), strong: true },
];

export function Legend({ layers = LAYERS.filter((l) => l !== "other"), extra }: { layers?: readonly Layer[]; extra?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted">
      {layers.map((l) => (
        <span key={l} className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: LAYER_COLOR[l] }} />
          {LAYER_SHORT[l]}
        </span>
      ))}
      {extra}
    </div>
  );
}

/**
 * One unit's cost as a horizontal stacked bar on a shared scale (`max` cents), with the price as a tick: the space
 * between the bar's end and the tick is the margin. Each segment explains its layer on hover.
 */
export function StackBar({ s, max, price, height = 12, label }: { s: Stack; max: number; price?: number | null; height?: number; label?: string }) {
  const scale = (c: number) => `${(Math.max(0, c) / Math.max(1, max)) * 100}%`;
  const shown = LAYERS.filter((l) => s.cents[l] > 0);
  const priceCents = price == null ? null : Math.round(price * 100);
  return (
    <div className="relative w-full" style={{ height }}>
      <div className="absolute inset-y-0 left-0 flex gap-[2px]" style={{ width: scale(s.totalCents) }}>
        {shown.map((l, i) => (
          <span key={l} className="block h-full" style={{ flex: `${s.cents[l]} 1 0`, minWidth: 2 }}>
            <Why title={`${label ?? s.sku}: ${LAYER_LABELS[l]}`} lines={layerLines(s, l)} width={400} className="block h-full">
              <span className={cx("block h-full hover:opacity-80", i === shown.length - 1 && "rounded-r-[4px]")} style={{ background: LAYER_COLOR[l] }} />
            </Why>
          </span>
        ))}
      </div>
      {priceCents != null && priceCents > 0 && (
        <span
          className="absolute -top-0.5 -bottom-0.5 w-[2px] rounded bg-text"
          style={{ left: `calc(${scale(priceCents)} - 1px)` }}
          title={`Price ${centsText(priceCents)}`}
        />
      )}
    </div>
  );
}

/**
 * The waterfall for one unit: Materials → +Assembly → +Packaging → +Freight & tariffs → +Tooling (+Other) = Landed cost,
 * against the price, leaving the margin. Floating columns on one baseline; each explains itself.
 */
export function Waterfall({ s, compare, height = 220 }: { s: Stack; compare?: Stack | null; height?: number }) {
  const layers = LAYERS.filter((l) => l !== "other" || s.cents.other !== 0);
  const price = s.price == null ? null : Math.round(s.price * 100);
  const top = Math.max(s.totalCents, price ?? 0, compare?.totalCents ?? 0, 1);
  const y = (c: number) => (c / top) * (height - 24);
  let run = 0;
  const steps = layers.map((l) => {
    const from = run;
    run += s.cents[l];
    return { l, from, to: run };
  });
  const margin = price == null ? null : price - s.totalCents;
  const col = "relative flex flex-1 flex-col items-center";
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(top * f));
  return (
    <div className="flex gap-3">
      <div className="relative w-12 shrink-0 text-right font-mono text-[10px] text-faint tabular-nums" style={{ height }}>
        {ticks.map((t) => (
          <span key={t} className="absolute right-0 -translate-y-1/2" style={{ bottom: y(t) + 18 }}>
            {centsText(t).replace(".00", "")}
          </span>
        ))}
      </div>
      <div className="relative flex min-w-0 flex-1 gap-2" style={{ height }}>
        {ticks.map((t) => (
          <span key={t} className="pointer-events-none absolute inset-x-0 h-px bg-line" style={{ bottom: y(t) + 18 }} />
        ))}
        {steps.map((st, i) => (
          <div key={st.l} className={col}>
            <Why
              title={`${i ? "+ " : ""}${LAYER_LABELS[st.l]}`}
              lines={layerLines(s, st.l)}
              width={420}
              className="absolute inset-x-[18%] inset-y-0 block no-underline hover:no-underline"
            >
              <span
                className="absolute inset-x-0 block rounded-t-[4px] hover:opacity-80"
                style={{ bottom: y(st.from) + 18, height: Math.max(2, y(st.to) - y(st.from)), background: LAYER_COLOR[st.l] }}
              />
            </Why>
            <span className="absolute font-mono text-[11px] text-text" style={{ bottom: y(st.to) + 20 }}>
              {centsText(s.cents[st.l])}
            </span>
            <span className="absolute bottom-0 text-center text-[11px] leading-tight text-muted">
              {i ? "+" : ""}
              {LAYER_SHORT[st.l]}
            </span>
          </div>
        ))}
        <div className={col}>
          <Why
            title="Landed cost per unit"
            lines={[
              ...layers.map((l) => ({ label: LAYER_LABELS[l], value: centsText(s.cents[l]) })),
              { label: "Landed cost", value: centsText(s.totalCents), strong: true },
            ]}
            note={compare ? `Against ${compare.kind === "planned" ? "planned" : "actual"} ${centsText(compare.totalCents)}` : undefined}
            className="absolute inset-x-[18%] inset-y-0 block no-underline hover:no-underline"
          >
            <span
              className="absolute inset-x-0 block rounded-t-[4px] bg-text/80 hover:opacity-80"
              style={{ bottom: 18, height: Math.max(2, y(s.totalCents)) }}
            />
          </Why>
          {compare && compare.totalCents > 0 && (
            <span
              className="pointer-events-none absolute inset-x-[10%] h-0 border-t-2 border-dashed border-muted"
              style={{ bottom: y(compare.totalCents) + 18 }}
              title={`${compare.kind === "planned" ? "Planned" : "Actual"} ${centsText(compare.totalCents)}`}
            />
          )}
          <span className="absolute font-mono text-[11px] font-semibold" style={{ bottom: y(Math.max(s.totalCents, compare?.totalCents ?? 0)) + 20 }}>
            {centsText(s.totalCents)}
          </span>
          <span className="absolute bottom-0 text-[11px] font-medium">= Landed</span>
        </div>
        {price != null && (
          <div className={col}>
            <Why
              title="Price and margin"
              lines={[
                { label: "Shopify price", value: centsText(price) },
                { label: "Landed cost", value: `−${centsText(s.totalCents)}` },
                { label: "Margin", value: `${centsText(margin ?? 0)} (${s.margin == null ? "—" : `${Math.round(s.margin * 100)}%`})`, strong: true },
              ]}
              note={s.complete ? undefined : "Some parts have no price, so the margin isn't shown"}
              className="absolute inset-x-[18%] inset-y-0 block no-underline hover:no-underline"
            >
              <span
                className="absolute inset-x-0 block rounded-t-[4px] border-2 border-text/70 bg-surface hover:bg-hover"
                style={{ bottom: 18, height: Math.max(2, y(price)) }}
              />
              {margin != null && margin > 0 && (
                <span
                  className="absolute inset-x-[2px] block"
                  style={{
                    bottom: y(s.totalCents) + 18,
                    height: Math.max(0, y(price) - y(s.totalCents) - 2),
                    background: "repeating-linear-gradient(135deg, var(--ok-soft) 0 4px, transparent 4px 8px)",
                  }}
                />
              )}
            </Why>
            <span className="absolute font-mono text-[11px]" style={{ bottom: y(price) + 20 }}>
              {centsText(price)}
            </span>
            <span className="absolute bottom-0 text-[11px] text-muted">Price</span>
          </div>
        )}
      </div>
    </div>
  );
}
